import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  MapPin,
  Clock,
  Calendar,
  Mail,
  User,
  Building,
  MessageSquare,
  Loader,
  Play,
} from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import { SelfGuidedTour } from '../components/SelfGuidedTour';
import { useForceLightMode } from '../hooks/useForceLightMode';

const trackEvent = (event: string) => {
  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible(event);
  }
};

export const DemoPage: React.FC = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || '';
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    organisation: '',
    role: '',
    message: '',
  });
  const [error, setError] = useState('');

  const videosRef = useRef<HTMLDivElement>(null);
  const bookRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement>, eventName?: string) => {
    if (eventName) trackEvent(eventName);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/notifications/contact/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(source ? { ...form, source } : form),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      trackEvent('demo_lead_submitted');
      navigate('/demo/schedule', { state: form });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
            See ClinLexis in action
          </h1>
          <p className="text-xl text-medical-600 mb-10 leading-relaxed font-light">
            Watch a self-guided tour, or book a personalised walkthrough with our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => scrollTo(bookRef, 'demo_cta_book')}
              className="inline-flex items-center justify-center space-x-2 px-7 py-3.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-lg shadow-primary-200/50 hover:shadow-xl hover:shadow-primary-300/50 transition-all text-base"
            >
              <Calendar className="h-5 w-5" />
              <span>Book a demo</span>
            </button>
            <button
              onClick={() => scrollTo(videosRef, 'demo_cta_watch')}
              className="inline-flex items-center justify-center space-x-2 px-7 py-3.5 rounded-lg bg-white border-2 border-primary-600 hover:bg-primary-50 text-primary-700 font-semibold transition-all text-base"
            >
              <Play className="h-5 w-5" />
              <span>Watch the videos</span>
            </button>
          </div>
          <p className="text-xs text-medical-500 mt-4">
            30-minute demo, or watch the self-guided tour at your own pace.
          </p>
        </div>
      </section>

      {/* VIDEOS — accordion list, compact by default, click to fold open */}
      <section ref={videosRef} id="videos" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SelfGuidedTour />

        {/* Bridge CTA into booking */}
        <div className="mt-16 text-center bg-white rounded-2xl border border-medical-200/60 px-8 py-10 shadow-sm">
          <h3 className="text-2xl font-bold text-medical-900 mb-3">Want a personalised walkthrough?</h3>
          <p className="text-medical-600 mb-6 max-w-xl mx-auto">
            Book a 30-minute demo with our team and we'll show you ClinLexis on your case types.
          </p>
          <button
            onClick={() => scrollTo(bookRef, 'demo_videos_to_book')}
            className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium shadow-lg shadow-primary-200/50 transition-all"
          >
            <Calendar className="h-4 w-4" />
            <span>Book a demo</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* BOOK */}
      <section
        ref={bookRef}
        id="book"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-medical-200/60 mt-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
              Book a personalised demo
            </h2>
            <p className="text-lg text-medical-600 mb-10 leading-relaxed font-light">
              A focused 30-minute walkthrough tailored to your practice area and case types.
            </p>

            <div className="space-y-6 mb-10">
              {[
                {
                  icon: Clock,
                  title: '30-minute walkthrough',
                  description: 'A focused demo tailored to your practice area and case types.',
                },
                {
                  icon: Calendar,
                  title: 'Flexible scheduling',
                  description: 'Choose a time that works for you. Available Monday to Friday.',
                },
                {
                  icon: MessageSquare,
                  title: 'Bring your questions',
                  description: "We'll cover your specific workflow needs and compliance requirements.",
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-medical-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-medical-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, label: 'NHS DSPT Compliant' },
                { icon: MapPin, label: 'UK Data Residency' },
              ].map((badge, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700"
                >
                  <badge.icon className="h-3 w-3" />
                  <span>{badge.label}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Right: Lead capture form (preserved from BookDemoPage) */}
          <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 p-8">
            <h2 className="text-2xl font-bold text-medical-900 mb-2">Tell us about you</h2>
            <p className="text-medical-600 mb-8">
              A few details and we'll take you straight to scheduling.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-medical-700 mb-1.5">
                  Full name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400" />
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400"
                    placeholder="Dr. Jane Smith"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-medical-700 mb-1.5">
                  Work email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400" />
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400"
                    placeholder="jane@example.co.uk"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="organisation" className="block text-sm font-medium text-medical-700 mb-1.5">
                  Organisation
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-medical-400" />
                  <input
                    id="organisation"
                    type="text"
                    required
                    value={form.organisation}
                    onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400"
                    placeholder="Smith & Partners Solicitors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-medical-700 mb-1.5">
                  Your role
                </label>
                <select
                  id="role"
                  required
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900"
                >
                  <option value="">Select your role</option>
                  <option value="solicitor">Solicitor</option>
                  <option value="barrister">Barrister</option>
                  <option value="paralegal">Paralegal / Legal Executive</option>
                  <option value="medical-expert">Medical Expert / Consultant</option>
                  <option value="claims-handler">Claims Handler</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-medical-700 mb-1.5">
                  Anything specific you'd like to see?{' '}
                  <span className="text-medical-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 border border-medical-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-medical-900 placeholder-medical-400 resize-none"
                  placeholder="e.g. clinical negligence workflows, guideline compliance checks..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5 text-base flex items-center justify-center space-x-2 group shadow-lg shadow-primary-200/50 hover:shadow-xl hover:shadow-primary-300/50 transition-all disabled:opacity-60"
              >
                {loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <span>Continue to scheduling</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis™. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
